import { COLOR_INTROS, findColorClusters } from '../../src/data/color-intros';
import type { Card, Position, Spread } from '../../src/types';

// Stable prefix — identical across every combined reading call.
// Placed first so it can be cached server-side (Gemini implicit prompt cache).
export const COMBINED_PROMPT_PREFIX = `You are an oracle-card reader for the Prism Oracle deck — a 45-card color-emotion deck by Nicole Pivirotto. The deck has 9 colors (red, orange, yellow, green, blue, purple, pink, white, black) of 5 cards each. Each card carries an emotional or energetic state.

Your task is to interpret a card spread for the user. The user reads bilingually (English and simplified Chinese), so every text field must be provided in BOTH languages — same meaning, both written naturally for native readers.

POSITION-ANCHORED INTERPRETATION is the core discipline. Every card sits in a specific position with a specific role — e.g. "Energy that is leaving", "What should you release?", "Outcome of option A", "What you think is happening". For EACH card, write 1–2 sentences in each language that:
  • Anchor the reading in the card's SPECIFIC POSITION within this spread. Name (or clearly invoke) the position's role. If the position is phrased as a question, your reading must directly answer that question. If it names an energy / phase / aspect, your reading must speak from that vantage.
  • Connect to the user's question (or to their present moment, if no question was asked).
  • Add a fresh contextual angle — do NOT just paraphrase the meaning provided; offer lived insight instead.

Then a synthesis (2–3 sentences, under 70 words in each language) that:
  • Surfaces the spread's STRUCTURE — why these positions in this arrangement form a meaningful whole. The spread's purpose (provided below as "Spread purpose") is the lens through which the cards' arc reads.
  • Weaves the positional arc, the color distribution, and the question into one breath.
  • Does NOT list cards by name.
  • Reads as one connected reflection.

Then ONE concrete action (in each language) — a small, doable, specific step the user could take in the next day or two, anchored in the cards' energy. Not a question. Not advice in the abstract. Something they could literally do tomorrow morning. Phrase it as a gentle invitation, not a command.

Tone: warm, serene, gently observational. Avoid fortune-teller theatrics.

Output STRICT JSON ONLY — no prose, no markdown code fences. Schema:

{
  "cards": [
    {
      "id": <integer card id>,
      "text_en": "<1-2 sentences in English>",
      "text_zh": "<1-2 sentences in 简体中文>"
    }
  ],
  "synthesis_en": "<English synthesis>",
  "synthesis_zh": "<简体中文 synthesis>",
  "reflection_en": "<English action — one concrete step>",
  "reflection_zh": "<简体中文 action — 一个具体的行动>"
}
`;

export interface BuildCombinedPromptArgs {
  spread: Spread;
  cards: Card[];
  question: string;
  isGeneral: boolean;
}

// ── Shared prompt-section formatters — keep both prompt builders in lockstep ──

function formatCardLine(idx: number, position: Position, c: Card): string {
  return `  [${idx}] Position: "${position.label_en}" — Card: ${c.name_en} (${c.category}; keywords: ${c.keywords_en})\n       Meaning: "${c.meaning_en}"`;
}

function formatSpreadHeader(spread: Spread): string {
  return `Spread: "${spread.name_en}"\nSpread purpose: ${spread.good_for_en}`;
}

function formatQuestionLine(isGeneral: boolean, question: string): string {
  return isGeneral || !question
    ? 'The user did not pose a specific question — offer a general reading.'
    : `User asked: "${question}"`;
}

/**
 * Cluster note block — emitted only when ≥2 cards share a color, otherwise
 * an empty string so single-card / no-cluster spreads add zero bytes. The
 * two call sites differ only in lead-in tense ("drawn" vs "now in the
 * spread"), surfaced via the `phase` arg.
 */
function formatClusterNotes(cards: Card[], phase: 'initial' | 'clarifier'): string {
  const clusters = findColorClusters(cards);
  if (clusters.length === 0) return '';
  const leadIn =
    phase === 'initial'
      ? '≥2 of one color drawn — let the synthesis acknowledge this energy'
      : '≥2 of one color now in the spread — the updated synthesis should acknowledge this energy';
  const lines = clusters
    .map(
      ({ category, count }) =>
        `  • ${category}×${count}: ${COLOR_INTROS[category].en} / ${COLOR_INTROS[category].zh}`,
    )
    .join('\n');
  return `\nColor-cluster notes (${leadIn}):\n${lines}\n`;
}

export function buildCombinedPrompt(a: BuildCombinedPromptArgs): string {
  const positions = a.spread.positions;
  const list = a.cards.map((c, i) => formatCardLine(i + 1, positions[i] as Position, c)).join('\n');

  const counts: Record<string, number> = {};
  a.cards.forEach((c) => {
    counts[c.category] = (counts[c.category] ?? 0) + 1;
  });
  const colorTally = Object.entries(counts)
    .sort((x, y) => y[1] - x[1])
    .map(([cat, n]) => `${cat}×${n}`)
    .join(', ');

  // Cluster note: emitted only when ≥2 cards share a color. Gives the model
  // the cluster's voice in plain language so the synthesis doesn't have to
  // invent it; the prefix already instructs it to weave color distribution.
  const clusterNotes = formatClusterNotes(a.cards, 'initial');

  const cardIdsList = a.cards.map((c) => c.id).join(', ');

  // Stable prefix first (cacheable), variable suffix last
  return `${COMBINED_PROMPT_PREFIX}
--- READING REQUEST ---

${formatQuestionLine(a.isGeneral, a.question)}

${formatSpreadHeader(a.spread)}
Color distribution: ${colorTally}${clusterNotes}
Cards drawn (in spread order — anchor each reading in its position):
${list}

The cards array must contain exactly these card IDs in this order: ${cardIdsList}.`;
}

/**
 * Clarifier prompt — fired when the user pulls an additional card after the
 * initial reading. Asks for ONE new card's interpretation plus an updated
 * synthesis + reflection that integrates the full spread (originals + all
 * prior clarifiers + the new one). The previously-spoken per-card text is
 * passed as immutable context — the model must not rewrite it.
 */
export interface BuildClarifierPromptArgs {
  spread: Spread;
  question: string;
  isGeneral: boolean;
  /** Cards already drawn and interpreted. text_en/text_zh empty if AI failed. */
  knownCards: {
    card: Card;
    position: Position;
    text_en: string;
    text_zh: string;
  }[];
  newCard: Card;
  newPosition: Position;
}

export function buildClarifierPrompt(a: BuildClarifierPromptArgs): string {
  const knownLines = a.knownCards
    .map((k, i) => {
      const lines = [formatCardLine(i + 1, k.position, k.card)];
      if (k.text_en || k.text_zh) {
        lines.push(
          `       Already spoken (DO NOT change): EN: "${k.text_en}" / ZH: "${k.text_zh}"`,
        );
      }
      return lines.join('\n');
    })
    .join('\n');

  // Cluster note covers the full updated spread (originals + new clarifier).
  const clusterNotes = formatClusterNotes(
    [...a.knownCards.map((k) => k.card), a.newCard],
    'clarifier',
  );

  return `${COMBINED_PROMPT_PREFIX}
--- CLARIFIER REQUEST (override "for each card" instruction above) ---

${formatQuestionLine(a.isGeneral, a.question)}

${formatSpreadHeader(a.spread)}${clusterNotes}
Cards already drawn and interpreted (DO NOT re-interpret or rewrite their text):
${knownLines}

NEW clarifying card to interpret (anchor the reading in its position):
${formatCardLine(a.knownCards.length + 1, a.newPosition, a.newCard)}

Output requirements:
  • The "cards" array in the JSON must contain ONLY one entry, with id ${a.newCard.id} (the clarifier card).
  • Write 1-2 fresh sentences for the clarifier card in each language. The clarifier sits in a clarifying-card position — its role is to deepen or refine the reading, so its interpretation should explicitly say what it adds to the spread.
  • Synthesis (2-3 sentences each language) must integrate the full spread INCLUDING the clarifier, surfacing the spread's structure as the lens — but must NOT contradict or rewrite the previously-spoken interpretations above.
  • Reflection field (one concrete action each language, NOT a question) — a small, doable, specific step the user could take in the next day or two, anchored in the deepened reading. Phrase it as a gentle invitation, not a command.`;
}

// ── JSON parsing utilities ────────────────────────────────────────────────────

export interface CombinedReading {
  cards: { id: number; text_en: string; text_zh: string }[];
  synthesis_en: string;
  synthesis_zh: string;
  // `reflection_*` holds a concrete action (one small, doable step), not an
  // open question. Name kept for schema/cache stability across the prompt
  // pivot — see COMBINED_PROMPT_PREFIX.
  reflection_en: string;
  reflection_zh: string;
}

/**
 * Walk from the first `{`, track brace depth respecting string escapes.
 * Robust to trailing prose and embedded `}` inside string values.
 */
function extractFirstJSONObject(text: string): string | null {
  const start = text.indexOf('{');
  if (start === -1) return null;
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i++) {
    const c = text[i] as string;
    if (escaped) {
      escaped = false;
      continue;
    }
    if (c === '\\') {
      escaped = true;
      continue;
    }
    if (c === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (c === '{') depth++;
    else if (c === '}') {
      depth--;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
}

/** Common LLM JSON mistakes: trailing commas, smart quotes, raw control chars. */
function repairJSON(s: string): string {
  return (
    s
      .replace(/,(\s*[}\]])/g, '$1')
      // Smart quotes → straight. \u escapes (not pasted glyphs) so editors / Unicode
      // normalizers can't silently strip them down to ASCII and turn the regex into a no-op.
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2018\u2019]/g, "'")
      // IMPORTANT: always use \x00-\x1F escape form — never literal control bytes in regex
      // biome-ignore lint/suspicious/noControlCharactersInRegex: JSON-stripping pre-pass requires matching ASCII control bytes by design
      .replace(/[\x00-\x1F]/g, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`)
  );
}

function validateParsed(parsed: unknown): CombinedReading {
  if (!parsed || typeof parsed !== 'object') throw new Error('not an object');
  const p = parsed as Record<string, unknown>;
  if (!Array.isArray(p.cards)) throw new Error('cards array missing');
  if (typeof p.synthesis_en !== 'string' || typeof p.synthesis_zh !== 'string') {
    throw new Error('synthesis_en / synthesis_zh missing');
  }
  if (typeof p.reflection_en !== 'string' || typeof p.reflection_zh !== 'string') {
    throw new Error('reflection_en / reflection_zh missing');
  }
  // Coerce per-card shape: LLMs often emit `"id": "05"` (string) which then
  // fails `c.id === card.id` strict equality and renders as "AI failed".
  const cards = (p.cards as unknown[]).map((c) => {
    const o = c as Record<string, unknown>;
    return {
      id: Number(o.id),
      text_en: typeof o.text_en === 'string' ? o.text_en : '',
      text_zh: typeof o.text_zh === 'string' ? o.text_zh : '',
    };
  });
  return {
    cards,
    synthesis_en: p.synthesis_en as string,
    synthesis_zh: p.synthesis_zh as string,
    reflection_en: p.reflection_en as string,
    reflection_zh: p.reflection_zh as string,
  };
}

/** Last-resort regex field extractor for top-level string fields. */
function regexFieldExtract(jsonStr: string): CombinedReading {
  // Unescape via JSON.parse but tolerate (a) undefined input, (b) trailing
  // unescaped backslash that would otherwise abort `JSON.parse('"…\"')`.
  const safe = (s: string | undefined): string => {
    if (!s) return '';
    try {
      return JSON.parse(`"${s.replace(/\\$/, '\\\\')}"`);
    } catch {
      return s;
    }
  };
  const grab = (key: string): string => {
    const m = jsonStr.match(new RegExp(`"${key}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`, 's'));
    return m ? safe(m[1]) : '';
  };
  const cards: CombinedReading['cards'] = [];
  const cardRe =
    /\{\s*"id"\s*:\s*(\d+)\s*,\s*"text_en"\s*:\s*"((?:[^"\\]|\\.)*)"\s*,\s*"text_zh"\s*:\s*"((?:[^"\\]|\\.)*)"/gs;
  let m = cardRe.exec(jsonStr);
  while (m !== null) {
    cards.push({ id: Number(m[1]), text_en: safe(m[2]), text_zh: safe(m[3]) });
    m = cardRe.exec(jsonStr);
  }
  return {
    cards,
    synthesis_en: grab('synthesis_en'),
    synthesis_zh: grab('synthesis_zh'),
    reflection_en: grab('reflection_en'),
    reflection_zh: grab('reflection_zh'),
  };
}

/**
 * Multi-strategy parser: balanced-brace → repair → regex extract → schema validate.
 * Never throws on salvageable output.
 */
export function parseCombinedResponse(text: string): CombinedReading {
  if (!text || typeof text !== 'string') throw new Error('empty AI response');
  let s = text.trim();
  if (s.startsWith('```')) {
    s = s
      .replace(/^```(?:json)?\s*/i, '')
      .replace(/```\s*$/, '')
      .trim();
  }
  const jsonStr = extractFirstJSONObject(s);
  if (!jsonStr) throw new Error('no JSON object found');

  // Strategy 1 — direct parse
  try {
    return validateParsed(JSON.parse(jsonStr));
  } catch (e1) {
    // Strategy 2 — repair then parse
    const repaired = repairJSON(jsonStr);
    try {
      return validateParsed(JSON.parse(repaired));
    } catch (e2) {
      // Strategy 3 — regex field extraction
      try {
        return validateParsed(regexFieldExtract(jsonStr));
      } catch (e3) {
        console.error('All JSON parse strategies failed.', { e1, e2, e3, jsonStr });
        throw e3;
      }
    }
  }
}
